/**
 * SignupPortalApp — root component for signup.oasisautokonnect.com.
 *
 * Internal URL-based routing (no react-router dep; same pattern as the
 * existing admin app). Pushes history state on navigation, listens to
 * popstate for browser back/forward.
 *
 * Routes:
 *   /                          IntakeForm (no affiliate attribution)
 *   /r/:referralCode           IntakeForm (affiliate-attributed)
 *   /auth?t=<token>            AuthLanding (magic link redemption fallback)
 *   /portal                    PortalHome (routes to next step)
 *   /portal/business           Step2Business
 *   /portal/payment            Step3Payment
 *   /portal/thanks             Thanks
 *   /portal/login              Login
 *   /portal/error              ErrorPage
 */

import { useCallback, useEffect, useState } from 'react';
import IntakeForm from './pages/IntakeForm';
import AuthLanding from './pages/AuthLanding';
import PortalHome from './pages/PortalHome';
import Step2Business from './pages/Step2Business';
import Step3Payment from './pages/Step3Payment';
import Thanks from './pages/Thanks';
import Login from './pages/Login';
import ErrorPage from './pages/ErrorPage';
import SignupHeader from './components/SignupHeader';
import SignupFooter from './components/SignupFooter';

export interface Route {
  kind:
    | 'intake'
    | 'auth'
    | 'portal_home'
    | 'business'
    | 'payment'
    | 'thanks'
    | 'login'
    | 'error';
  referralCode?: string;
  authToken?: string;
  errorReason?: string;
}

function parseRoute(): Route {
  const path = window.location.pathname;
  const search = window.location.search;
  const params = new URLSearchParams(search);

  // /r/:referralCode (case-preserving)
  const referralMatch = path.match(/^\/r\/([A-Za-z0-9_-]+)\/?$/);
  if (referralMatch) {
    return { kind: 'intake', referralCode: referralMatch[1] };
  }
  if (path === '/' || path === '') {
    return { kind: 'intake' };
  }
  if (path === '/auth' || path === '/auth/') {
    return { kind: 'auth', authToken: params.get('t') || undefined };
  }
  if (path === '/portal' || path === '/portal/') {
    return { kind: 'portal_home' };
  }
  if (path === '/portal/business' || path === '/portal/business/') {
    return { kind: 'business' };
  }
  if (path === '/portal/payment' || path === '/portal/payment/') {
    return { kind: 'payment' };
  }
  if (path === '/portal/thanks' || path === '/portal/thanks/') {
    return { kind: 'thanks' };
  }
  if (path === '/portal/login' || path === '/portal/login/') {
    return { kind: 'login' };
  }
  if (path === '/portal/error' || path === '/portal/error/') {
    return { kind: 'error', errorReason: params.get('reason') || undefined };
  }
  // Unknown route → land on intake
  return { kind: 'intake' };
}

export default function SignupPortalApp() {
  const [route, setRoute] = useState<Route>(() => parseRoute());

  const navigate = useCallback((newPath: string) => {
    window.history.pushState({}, '', newPath);
    setRoute(parseRoute());
    window.scrollTo({ top: 0 });
  }, []);

  useEffect(() => {
    const onPopState = () => setRoute(parseRoute());
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    document.title = 'OAK Client Registration';
  }, []);

  const renderRoute = () => {
    switch (route.kind) {
      case 'intake':
        return <IntakeForm referralCode={route.referralCode} navigate={navigate} />;
      case 'auth':
        return <AuthLanding authToken={route.authToken} navigate={navigate} />;
      case 'portal_home':
        return <PortalHome navigate={navigate} />;
      case 'business':
        return <Step2Business navigate={navigate} />;
      case 'payment':
        return <Step3Payment navigate={navigate} />;
      case 'thanks':
        return <Thanks navigate={navigate} />;
      case 'login':
        return <Login navigate={navigate} />;
      case 'error':
        return <ErrorPage reason={route.errorReason} navigate={navigate} />;
      default:
        return <IntakeForm navigate={navigate} />;
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 dark:bg-slate-950 transition-colors">
      <SignupHeader />
      <main className="flex-1 w-full">
        <div className="max-w-3xl mx-auto px-4 py-8">{renderRoute()}</div>
      </main>
      <SignupFooter />
    </div>
  );
}
